/** Build a public LinkedIn feed URL from a post URN, social id, or share URL. */
export function buildLinkedInPostUrl(idOrUrn?: string | null): string {
  const raw = (idOrUrn || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;

  let urn = raw;
  if (!urn.startsWith('urn:li:')) {
    if (/^\d+$/.test(urn)) urn = `urn:li:activity:${urn}`;
    else if (urn.includes(':')) urn = `urn:li:${urn}`;
    else urn = `urn:li:activity:${urn}`;
  }

  return `https://www.linkedin.com/feed/update/${encodeURIComponent(urn)}`;
}

export type ParsedLinkedInPost = {
  kind: 'ugcPost' | 'activity';
  id: string;
  ugcPostId: string;
};

/** Extract activity/ugcPost id from a LinkedIn post URL, URN, or raw id. */
export function parseLinkedinPostInput(input = ''): ParsedLinkedInPost | null {
  const value = String(input || '').trim();
  if (!value) return null;

  // Prefer activity — used as dedup / comments API key for both URL formats.
  const activityMatch =
    value.match(/urn:li:activity:(\d+)/i) ||
    value.match(/activity-(\d+)/i) ||
    value.match(/activity[=:](\d+)/i);
  if (activityMatch?.[1]) {
    return { kind: 'activity', id: activityMatch[1], ugcPostId: `urn:li:activity:${activityMatch[1]}` };
  }

  const ugcMatch =
    value.match(/urn:li:ugcPost:(\d+)/i) ||
    value.match(/ugcPost[-:=](\d+)/i);
  if (ugcMatch?.[1]) {
    return { kind: 'ugcPost', id: ugcMatch[1], ugcPostId: `urn:li:ugcPost:${ugcMatch[1]}` };
  }

  // /posts/...-share-ID-xxap/ or ...-share-ID-_pkC/ (suffix may include _)
  const shareSlugMatch = value.match(
    /linkedin\.com\/posts\/[^?#]*?-share-(\d{10,})-[A-Za-z0-9_]+-?\/?(?:[?#]|$)/i
  );
  if (shareSlugMatch?.[1]) {
    return {
      kind: 'ugcPost',
      id: shareSlugMatch[1],
      ugcPostId: `urn:li:ugcPost:${shareSlugMatch[1]}`,
    };
  }

  // Other /posts/...-ID-code/ permalinks → activity
  const postsSlugMatch = value.match(
    /linkedin\.com\/posts\/[^?#]*?-(\d{10,})-[A-Za-z0-9_]+-?\/?(?:[?#]|$)/i
  );
  if (postsSlugMatch?.[1]) {
    return {
      kind: 'activity',
      id: postsSlugMatch[1],
      ugcPostId: `urn:li:activity:${postsSlugMatch[1]}`,
    };
  }

  if (/^\d+$/.test(value)) {
    return { kind: 'activity', id: value, ugcPostId: `urn:li:activity:${value}` };
  }

  return null;
}

/** True when the value looks like a LinkedIn post / activity / ugcPost URL or URN. */
export function isValidLinkedInPostUrl(value?: string | null): boolean {
  const raw = (value || '').trim();
  if (!raw) return false;
  if (parseLinkedinPostInput(raw)) return true;
  if (!/^https?:\/\//i.test(raw)) return false;
  try {
    const u = new URL(raw);
    if (!/(^|\.)linkedin\.com$/i.test(u.hostname)) return false;
    const path = u.pathname;
    return (
      /\/feed\/update\//i.test(path)
      || /\/posts\//i.test(path)
      || /\/pulse\//i.test(path)
      || /ugcPost|activity|share/i.test(path + u.search + u.hash)
    );
  } catch {
    return false;
  }
}
